import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolStep,
  ProtocolWarning,
} from "../types";

/**
 * Urinary hCG, which is the same trigger as Ovidrel and a different procedure.
 *
 * Ovidrel arrives ready to inject. This arrives as a powder and a diluent, and
 * everything the coach has not had to guide until now is in the gap between
 * them: mixing a vial, changing a needle, and — on the route the US label names
 * — putting a 38 mm needle into the upper outer quadrant of her own buttock and
 * pulling back on the plunger before pushing.
 *
 * ### Why there are two protocols and not one
 *
 * The content describes one vial and two routes, with the subcutaneous one as an
 * alternative sentence at the end. Those are not one procedure with a footnote:
 * the site is different, the needle is different, and aspiration belongs to one
 * of them and not the other. A walk that offered both would be a walk that let
 * her inject in the wrong place, which is the failure this whole package is
 * shaped against. So the route is chosen before the walk starts — from her
 * prescription when it records one, and from her when it does not.
 *
 * ### What the evidence says about each of these steps
 *
 * `docs/features/injection-coach/TECHNIQUE.md` carries the tiers, and two of
 * them decide wording here:
 *
 * - **Aspiration is contested everywhere except here.** A systematic review
 *   found it unnecessary at the deltoid, ventrogluteal and vastus lateralis
 *   sites — and *recommends* it at the dorsogluteal site, because of the gluteal
 *   artery. The dorsogluteal site is the one the content names and the one
 *   fertility patients actually use, so the instruction stays, and it is stated
 *   as the content states it rather than expanded.
 * - **The site itself is tier P/E, not label.** Ventrogluteal is better
 *   evidenced for comfort; the upper outer quadrant is what the labels name and
 *   what a patient can find on herself. The coach does not adjudicate that. It
 *   says what the approved content says, and the clinic's own instruction wins —
 *   which is the first claim of the disclosure she has already read.
 *
 * ### One word that is not the content's
 *
 * The content says "IM" — twenty-four times, in both languages. The steps say
 * "intramuscular" / "intramusculaire" in full (Anaïs, 2026-09-22). An
 * abbreviation is fine in a leaflet read at leisure and wrong on a card read
 * with a needle in hand by someone meeting the word for the first time; the
 * content names the route in full itself, in `commonRoutes`, so this is its own
 * vocabulary spelled out rather than a term the coach invented.
 *
 * Everything else below omits from the approved content and adds nothing to it,
 * which `pregnyl-protocol.test.ts` holds word by word.
 */

const PREGNYL_BRANDS = [
  {
    name: "Pregnyl",
    markets: [
      "US",
      "CA",
      "GB",
      "AU",
      "NL",
      "EU",
      "SG",
      "MY",
      "PH",
      "ID",
      "VN",
      "IN",
    ],
  },
  { name: "Novarel", markets: ["US"] },
  { name: "Brevactid", markets: ["DE", "EU"] },
  { name: "Choragon", markets: ["DE", "GB", "MT"] },
  { name: "Choriomon", markets: ["CH"] },
  { name: "Gonasi HP", markets: ["IT"] },
  { name: "Zivafert", markets: ["GB"] },
  { name: "Hucog-HP", markets: ["IN"] },
  { name: "Corion", markets: ["IN"] },
  { name: "LG IVF-C", markets: ["IN"] },
] as const satisfies readonly ProtocolBrand[];

/**
 * From the content's own alias list, minus two of them.
 *
 * `Choragon` is a trade name with markets, so it belongs in `brands` — where it
 * already is, and where it can be checked against where it is actually sold.
 *
 * `Gonadotrophine chorionique` is dropped for a harder reason: it is the French
 * name of the molecule *class*, and Ovidrel's own French content uses it —
 * "Gonadotrophine chorionique humaine (recombinante)". Matching it here sent a
 * patient holding a recombinant prefilled syringe into a walk that reconstitutes
 * a powder and, on one route, injects into her buttock. A name that two
 * medications answer to identifies neither, and this package's rule for that is
 * silence, not a guess.
 */
const PREGNYL_ALIASES = ["Urinary hCG", "hCG urinaire"] as const;

/**
 * The warnings both routes carry.
 *
 * Split the way Ovidrel's are, and for the same reason: the content phrases a
 * spilled dose and difficulty breathing as one sentence in some variants, and
 * the coach must not route a breathing difficulty to "call your clinic" because
 * a sentence put the two together. Each `sourceSegment` is a ⚠ segment present
 * verbatim in the Pregnyl entry.
 */
const MEDICATION_WARNINGS: ProtocolWarning[] = [
  {
    id: "pregnyl.spill-or-incomplete-dose",
    // Still a `stop` — a dose that may not have gone in is not something to
    // walk past — but no longer `clinic-now`. A spilled or partial dose needs
    // a call, not a panic: the wording was "call your clinic IMMEDIATELY" in
    // capitals, which reads as an emergency to someone already frightened, and
    // an incomplete trigger is a conversation with her clinic rather than a
    // race (Anaïs, 2026-09-22). The content was changed to match, since the
    // protocol may not soften a sentence the clinical document shouts.
    severity: "stop",
    escalation: "clinic-soon",
    copy: {
      en: "Spill or incomplete dose - If you spill any medication or suspect an incomplete dose, call your clinic as soon as you can.",
      fr: "Renversement ou dose incomplète - Si vous renversez du produit ou suspectez une dose incomplète, appelez votre centre dès que possible.",
    },
    sourceSegment:
      "⚠ Spill or incomplete dose - If you spill any medication or suspect an incomplete dose, call your clinic as soon as you can.",
  },
  {
    id: "pregnyl.breathing-or-allergic-reaction",
    // Emergency services, and the sentence says so. It used to read "Contact
    // your clinic for severe bloating, difficulty breathing, or signs of
    // allergic reaction" — one sentence, three symptoms, one destination, and
    // the destination was wrong for the one that kills quickly. Anaphylaxis is
    // not a phone call to a fertility clinic (Anaïs, 2026-09-22), and the
    // content was changed to say so before this restated it.
    severity: "stop",
    escalation: "emergency",
    copy: {
      en: "Seek urgent help - Difficulty breathing or signs of an allergic reaction need emergency services straight away.",
      fr: "Aide urgente - Des difficultés respiratoires ou des signes de réaction allergique nécessitent d'appeler les secours tout de suite.",
    },
    sourceSegment:
      "⚠ Seek urgent help - Difficulty breathing or signs of an allergic reaction need emergency services straight away.",
  },
  {
    id: "pregnyl.severe-bloating",
    // The other half of that sentence, and it keeps the clinic: bloating after
    // a trigger is the beginning of OHSS, which her own clinic manages and
    // wants to hear about the same day.
    severity: "stop",
    escalation: "clinic-now",
    copy: {
      en: "Severe bloating - Call your clinic the same day for severe bloating.",
      fr: "Ballonnements sévères - Appelez votre centre le jour même en cas de ballonnements sévères.",
    },
    sourceSegment:
      "⚠ Severe bloating - Call your clinic the same day for severe bloating.",
  },
];

/**
 * Said on the step that creates the problem it describes.
 *
 * A reconstituted vial has a shelf life of nothing. The content states it as a
 * warning rather than as part of the mixing step, and it belongs beside the
 * mixing: a patient who reads it at the end has already waited.
 */
const USE_IMMEDIATELY: ProtocolWarning = {
  id: "pregnyl.use-immediately",
  severity: "caution",
  escalation: "none",
  // Said without the content's heading. A step's heading earns its place —
  // `splitStepCopy` makes it the title of the card and the sentence follows it.
  // A warning is rendered whole, in one bubble, so "Use immediately -" is a
  // label sitting in front of a sentence that already says it (Anaïs,
  // 2026-09-22). Omitting is what a protocol is allowed to do; the segment it
  // was read from is unchanged below.
  copy: {
    en: "{brand} must be used immediately after reconstitution. Do not store the reconstituted solution.",
    fr: "Le {brand} doit être utilisé immédiatement après reconstitution. Ne pas conserver la solution préparée.",
  },
  sourceSegment:
    "⚠ Use immediately - Pregnyl must be used immediately after reconstitution. Do not store the reconstituted solution.",
};

/**
 * Timing and mixing, identical whichever route follows.
 *
 * Built per protocol rather than shared as one array, because a step id names
 * the protocol it belongs to — `ovidrel.pen.clean-site` and
 * `ovidrel.syringe.clean-site` are two different steps with the same name, and
 * a session records which one guided it.
 */
const sharedPreparation = (route: "im" | "subq"): ProtocolStep[] => [
  {
    // Shortened the way Ovidrel's is, and for the reason Anaïs settled there:
    // the hour is the clinic's, and the retrieval interval is not what she
    // needs while she is about to mix a vial.
    id: `pregnyl.${route}.critical-timing`,
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Critical timing - Inject at the EXACT time your clinic prescribed.",
      fr: "Timing essentiel - Injectez à l'heure EXACTE prescrite par votre centre.",
    },
  },
  {
    // The step the first draft skipped, and the only procedure in the catalogue
    // that was missing one: every other walk washes her hands before it asks
    // her to touch anything. This one opened on a clock and then said "using an
    // 18G drawing needle" — a needle nothing had told her to have out.
    id: `pregnyl.${route}.gather-supplies`,
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en:
        route === "im"
          ? "Gather supplies - Wash hands and collect: {brand} powder vial, diluent, an 18G drawing needle, a syringe, and a 22G 1.5-inch needle for the intramuscular injection."
          : "Gather supplies - Wash hands and collect: {brand} powder vial, diluent, an 18G drawing needle, a syringe, and a 27-30G ½-inch needle.",
      fr:
        route === "im"
          ? "Préparer le matériel - Lavez-vous les mains et préparez : le flacon de poudre {brand}, le diluant, une aiguille pompeuse 18 G, une seringue, et une aiguille d'injection 22 G, 38 mm pour l'injection intramusculaire."
          : "Préparer le matériel - Lavez-vous les mains et préparez : le flacon de poudre {brand}, le diluant, une aiguille pompeuse 18 G, une seringue, et une aiguille 27-30 G, 12 mm.",
    },
  },
  {
    id: `pregnyl.${route}.reconstitute`,
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Reconstitute - Using an 18G drawing needle, inject the provided diluent into the powder vial. Swirl gently until fully dissolved - never shake. Confirm the solution is clear with no visible particles before proceeding.",
      fr: "Reconstituer - Avec une aiguille pompeuse 18 G, injectez le diluant fourni dans le flacon de poudre. Faites tourner doucement jusqu'à dissolution complète - sans agiter. Vérifiez que la solution est claire et sans particules avant de poursuivre.",
    },
    warnings: [USE_IMMEDIATELY],
  },
];

export const PREGNYL_INTRAMUSCULAR_PROTOCOL: InjectionProtocol = {
  medicationSlug: "pregnyl",
  brands: PREGNYL_BRANDS,
  // Urinary hCG reaches prescriptions under its molecule as often as under a
  // trade name. Recognition only — see `ProtocolAlias`.
  aliases: PREGNYL_ALIASES,
  indication: "trigger",
  // International units, and no fixed list: a 10,000 IU vial is given as
  // 5,000 or 10,000 depending on the protocol, and a clinic may prescribe
  // between. The unit is the only thing a record can get wrong here.
  dose: { unit: "IU" },
  form: "vial",
  prescriptionForms: ["lyophilized_powder"],
  route: "intramuscular",
  version: "2026-09-22.1",
  derivedFrom: "content/data/medications/trigger-shots.json#pregnyl",
  steps: [
    ...sharedPreparation("im"),
    {
      id: "pregnyl.im.draw-up-the-dose",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Draw up the dose - Draw the full reconstituted solution into the syringe using the 18G needle, then switch to a 22G, 1.5-inch needle for the intramuscular injection.",
        fr: "Prélever la dose - Aspirez la totalité de la solution reconstituée dans la seringue avec l'aiguille 18 G, puis changez pour une aiguille 22 G, 38 mm pour l'injection intramusculaire.",
      },
    },
    {
      id: "pregnyl.im.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Clean the intramuscular site - Clean the upper outer quadrant of the buttock with an alcohol swab and let it air-dry completely.",
        fr: "Désinfecter le site intramusculaire - Désinfectez le quadrant supéro-externe de la fesse à l'alcool et laissez sécher complètement à l'air.",
      },
    },
    {
      // The one step that cannot be undone, and the only one in the catalogue
      // that asks her to check something *during* the injection rather than
      // before it. Aspiration is stated as the content states it: pull back,
      // and if blood appears this site is not the one.
      id: "pregnyl.im.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject (intramuscular) - Insert the needle at 90° and aspirate briefly by pulling back the plunger slightly. If blood appears, withdraw and choose a different site. Inject slowly once confirmed clear.",
        fr: "Injecter (intramusculaire) - Insérez l'aiguille à 90° et aspirez brièvement en tirant légèrement le piston. Si du sang apparaît, retirez et choisissez un autre site. Injectez lentement une fois la voie confirmée.",
      },
      warnings: MEDICATION_WARNINGS,
    },
    {
      id: "pregnyl.im.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Apply gentle pressure and use a warm compress on the intramuscular site to ease soreness. Dispose of all sharps in a sharps container.",
        fr: "Après l'injection - Appliquez une légère pression et utilisez une compresse tiède sur le site intramusculaire pour soulager la sensibilité. Jetez le matériel dans un collecteur.",
      },
    },
  ],
};

export const PREGNYL_SUBCUTANEOUS_PROTOCOL: InjectionProtocol = {
  medicationSlug: "pregnyl",
  brands: PREGNYL_BRANDS,
  // Urinary hCG reaches prescriptions under its molecule as often as under a
  // trade name. Recognition only — see `ProtocolAlias`.
  aliases: PREGNYL_ALIASES,
  indication: "trigger",
  // International units, and no fixed list: a 10,000 IU vial is given as
  // 5,000 or 10,000 depending on the protocol, and a clinic may prescribe
  // between. The unit is the only thing a record can get wrong here.
  dose: { unit: "IU" },
  form: "vial",
  prescriptionForms: ["lyophilized_powder"],
  route: "subcutaneous",
  version: "2026-09-22.1",
  derivedFrom: "content/data/medications/trigger-shots.json#pregnyl",
  steps: [
    ...sharedPreparation("subq"),
    {
      // The same draw-up with the needle the subcutaneous route takes. Both
      // needle specifications come from the content's own alternative.
      id: "pregnyl.subq.draw-up-the-dose",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Draw up the dose - Draw the full reconstituted solution into the syringe using the 18G needle, then use a 27-30G, ½-inch needle in the lower abdomen instead of an intramuscular injection.",
        fr: "Prélever la dose - Aspirez la totalité de la solution reconstituée dans la seringue avec l'aiguille 18 G, puis utilisez une aiguille 27-30 G, 12 mm dans le bas du ventre plutôt qu'en intramusculaire.",
      },
    },
    {
      id: "pregnyl.subq.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Clean the site - Clean a spot on the lower abdomen with an alcohol swab and let it air-dry completely.",
        fr: "Désinfecter le site - Désinfectez un point sur le bas du ventre à l'alcool et laissez sécher complètement à l'air.",
      },
    },
    {
      // No aspiration here, and its absence is the point. The review that
      // recommends it names the dorsogluteal site and only that one; carrying
      // it into a subcutaneous injection would be the coach inventing a step.
      id: "pregnyl.subq.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Pinch skin on the lower abdomen and inject at 90°.",
        fr: "Injecter - Pincez la peau du bas du ventre et injectez à 90°.",
      },
      warnings: MEDICATION_WARNINGS,
    },
    {
      id: "pregnyl.subq.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Apply gentle pressure. Dispose of all sharps in a sharps container.",
        fr: "Après l'injection - Appliquez une légère pression. Jetez le matériel dans un collecteur.",
      },
    },
  ],
};

export const PREGNYL_PROTOCOLS = [
  PREGNYL_INTRAMUSCULAR_PROTOCOL,
  PREGNYL_SUBCUTANEOUS_PROTOCOL,
] as const;
